import { supabase } from '../../shared/services/supabase';
import { netlifyFetch } from '../../shared/services/netlifyFetch';
import { authService } from '../../shared/services/auth.service';
import { FileStorageResult, FileUploadOptions, STORAGE_CONFIG } from './types';

export class FileStorageService {
  
  /**
   * Prüft ob eine Datei die Größenbeschränkung überschreitet
   */
  isFileTooBig(file: File): boolean {
    const fileSizeMB = file.size / (1024 * 1024);
    return fileSizeMB > STORAGE_CONFIG.MAX_ATTACHMENT_SIZE_MB;
  }

  /**
   * Prüft ob die Gesamtgröße aller Dateien die Grenze überschreitet
   */
  isTotalSizeTooBig(files: File[]): boolean {
    const totalSizeMB = files.reduce((total, file) => total + file.size, 0) / (1024 * 1024);
    return totalSizeMB > STORAGE_CONFIG.MAX_TOTAL_SIZE_MB;
  }

  /**
   * Stellt sicher, dass Supabase authentifiziert ist
   */
  private async ensureAuthenticated(): Promise<boolean> {
    try {
      // Prüfe aktuelle Session
      const { data: { session } } = await supabase.auth.getSession();
      
      if (session) {
        console.log('Supabase session exists for storage access');
        return true;
      }

      // Keine Session - versuche Auto-Login
      console.log('No Supabase session found, attempting auto-login...');
      const loginSuccess = await authService.autoLogin();
      
      if (!loginSuccess) {
        console.error('Supabase authentication failed for storage access');
        return false;
      }

      // Prüfe Session nach Auto-Login
      const { data: { session: newSession } } = await supabase.auth.getSession();
      return !!newSession;

    } catch (error) {
      console.error('Authentication check failed:', error);
      return false;
    }
  }

  /**
   * Lädt eine Datei in den Supabase Storage hoch
   */
  async uploadFile(
    file: File, 
    fileName: string, 
    options?: FileUploadOptions
  ): Promise<FileStorageResult> {
    try {
      // Prüfe Storage-spezifische Limits
      const fileSizeMB = file.size / (1024 * 1024);
      if (fileSizeMB > STORAGE_CONFIG.MAX_STORAGE_SIZE_MB) {
        return {
          success: false,
          error: `Datei zu groß für Storage (${fileSizeMB.toFixed(2)}MB). Maximum: ${STORAGE_CONFIG.MAX_STORAGE_SIZE_MB}MB`
        };
      }

      // Stelle sicher, dass wir authentifiziert sind
      const isAuthenticated = await this.ensureAuthenticated();
      if (!isAuthenticated) {
        return {
          success: false,
          error: 'Supabase-Authentifizierung für Storage-Zugriff fehlgeschlagen'
        };
      }

      // Generiere eindeutigen Pfad
      const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
      const sanitizedFileName = fileName.replace(/[^a-zA-Z0-9.-]/g, '_');
      const filePath = options?.customPath || `${timestamp}/${sanitizedFileName}`;

      console.log(`Uploading file to storage: ${fileName} (${fileSizeMB.toFixed(2)}MB) to path: ${filePath}`);

      // Upload zu Supabase Storage mit Timeout-Handling
      const uploadPromise = supabase.storage
        .from(STORAGE_CONFIG.BUCKET_NAME)
        .upload(filePath, file, {
          cacheControl: '3600',
          upsert: options?.upsert || false
        });

      // 30 Sekunden Timeout für große Dateien
      const timeoutPromise = new Promise((_, reject) => 
        setTimeout(() => reject(new Error('Upload timeout after 30 seconds')), 30000)
      );

      const { error } = await Promise.race([uploadPromise, timeoutPromise]) as any;

      if (error) {
        console.error('Storage upload error:', error);
        
        // Spezielle Behandlung für Gateway Timeouts
        if (error.message.includes('504') || error.message.includes('timeout')) {
          return {
            success: false,
            error: `Storage-Upload Timeout: Datei zu groß für Supabase Storage (${fileSizeMB.toFixed(2)}MB)`
          };
        }

        return {
          success: false,
          error: `Upload fehlgeschlagen: ${error.message}`
        };
      }

      // Signierten, befristeten Download-Link erzeugen (23.09.2026). Der Link
      // entsteht nicht mehr im Browser, sondern in der Netlify-Funktion
      // download-link, die Signatur und Ablaufdatum anhängt; download.js prüft
      // beides, bevor es die Datei freigibt. Ohne gültigen Link gilt der Upload
      // als gescheitert, damit keine Mail mit einem toten Link rausgeht.
      const linkResponse = await netlifyFetch('/.netlify/functions/download-link', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ path: filePath })
      });
      const linkResult = await linkResponse.json().catch(() => ({}));
      if (!linkResponse.ok || !linkResult.success || !linkResult.url) {
        console.error('Download-Link konnte nicht erzeugt werden:', linkResult.error || `HTTP ${linkResponse.status}`);
        return {
          success: false,
          error: `Download-Link konnte nicht erzeugt werden: ${linkResult.error || `HTTP ${linkResponse.status}`}`
        };
      }
      const xtaxDownloadUrl: string = linkResult.url;

      console.log(`Storage upload successful with signed XTAX download URL for path: ${filePath}`);

      return {
        success: true,
        publicUrl: xtaxDownloadUrl
      };

    } catch (error) {
      console.error('File upload error:', error);
      
      // Spezielle Behandlung für Timeout-Fehler
      if (error instanceof Error && (
        error.message.includes('timeout') || 
        error.message.includes('504') ||
        error.message.includes('Gateway Timeout')
      )) {
        return {
          success: false,
          error: `Storage-Upload zu langsam: Datei wahrscheinlich zu groß für Supabase Storage`
        };
      }

      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unbekannter Upload-Fehler'
      };
    }
  }

  /**
   * Generiert einen Download-Link für eine bereits hochgeladene Datei
   */
  getPublicUrl(filePath: string): string {
    const { data } = supabase.storage
      .from(STORAGE_CONFIG.BUCKET_NAME)
      .getPublicUrl(filePath);
    
    return data.publicUrl;
  }

  /**
   * Löscht eine Datei aus dem Storage
   */
  async deleteFile(filePath: string): Promise<boolean> {
    try {
      const { error } = await supabase.storage
        .from(STORAGE_CONFIG.BUCKET_NAME)
        .remove([filePath]);

      if (error) {
        console.error('Storage delete error:', error);
        return false;
      }

      return true;
    } catch (error) {
      console.error('File delete error:', error);
      return false;
    }
  }
}